import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isIframe, useIframeMessageContext } from '../IframeMessageContext';
import { useSendEditing } from './iframe-messaging/useSendEditing';
import { useSendEditPath } from './iframe-messaging/useSendEditPath';
import { useSendHoverComponent } from './iframe-messaging/useSendHoverComponent';
import { useSendIframeReady } from './iframe-messaging/useSendIframeReady';
import { useSendSelectedComponent } from './iframe-messaging/useSendSelectedComponent';
import { useSendSite } from './iframe-messaging/useSendSite';
export var ViewMode;
(function (ViewMode) {
    ViewMode["Fill"] = "Fill";
    ViewMode["Desktop"] = "Desktop";
    ViewMode["Tablet"] = "Tablet";
    ViewMode["Mobile"] = "Mobile";
})(ViewMode || (ViewMode = {}));
export function useSiteConfigurator({ notifyComponentUpdate, siteComponentMap }) {
    // leverage site context to manage iframe ready state from iframe message
    // context since site context handles messaging
    const { iframeReady, setIframeReady } = useIframeMessageContext();
    const [siteConfigState, setSiteConfigState] = useState({
        viewMode: ViewMode.Desktop
    });
    const { site, editing, viewMode, editPath } = siteConfigState;
    // useRef to prevent component tree from rerendering all components
    const selectedComponentRef = useRef();
    const hoverComponentRef = useRef();
    // state to notify the site configurator (outside iframe only); this is
    // separated to prevent iframe rerendering
    const [configureComponent, setConfigureComponent] = useState();
    // iframe messaging
    const { sendSite } = useSendSite();
    const { sendEditing } = useSendEditing();
    const { sendIframeReady } = useSendIframeReady();
    const { sendEditPath } = useSendEditPath();
    const { sendHoverComponent } = useSendHoverComponent();
    const { sendSelectedComponent } = useSendSelectedComponent();
    // send site config to iframe when iframe is ready to receive messages
    useEffect(() => {
        if (iframeReady) {
            sendIframeReady();
            if (!isIframe()) {
                sendEditing(Boolean(editing));
            }
        }
    }, [editing, iframeReady, sendEditing, sendIframeReady]);
    // get root component of the selected component
    const type = configureComponent === null || configureComponent === void 0 ? void 0 : configureComponent.type; // watch for type changes
    const selectedComponent = selectedComponentRef.current;
    const selectedRootComponent = useMemo(() => {
        const getRootComponent = (component) => {
            return component.parent ? getRootComponent(component.parent) : component;
        };
        if (selectedComponent && type) {
            return getRootComponent(selectedComponent);
        }
        else {
            return undefined;
        }
    }, [selectedComponent, type]);
    /**
     * Set the view mode (fit, desktop, tablet, or mobile) in the PreviewFrame
     * component.
     */
    const setViewMode = useCallback((viewMode) => setSiteConfigState(siteConfigState => (Object.assign(Object.assign({}, siteConfigState), { viewMode }))), []);
    /**
     * Ability to toggle between edit and view mode. Edit mode allows clicking to
     * select components in the PreviewFrame. View mode allows interacting with
     * the site as a normal user.
     */
    const toggleEdit = useCallback((isEdit, options) => {
        const { shouldSendIframe = true } = options || {};
        setSiteConfigState(siteConfigState => (Object.assign(Object.assign({}, siteConfigState), { editing: isEdit })));
        if (shouldSendIframe)
            sendEditing(isEdit);
    }, [sendEditing]);
    /**
     * Set the site being edited
     */
    const setSite = useCallback((site, options) => {
        const { shouldSendIframe = true } = options || {};
        // let siteChanged = false;
        setSiteConfigState(siteConfigState => {
            // siteChanged = siteConfigState.site?.id !== site.id;
            return Object.assign(Object.assign({}, siteConfigState), { site });
        });
        if (shouldSendIframe)
            sendSite(site);
    }, [sendSite, setSiteConfigState]);
    /**
     * Set the route path being edited
     */
    const setEditPath = useCallback((path, options) => {
        const { shouldSendIframe = true } = options || {};
        let editPathChanged = false;
        setSiteConfigState(siteConfigState => {
            editPathChanged = siteConfigState.editPath !== path;
            return editPathChanged
                ? Object.assign(Object.assign({}, siteConfigState), { editPath: path }) : siteConfigState;
        });
        if (editPathChanged && path && shouldSendIframe)
            sendEditPath(path);
    }, [sendEditPath, setSiteConfigState]);
    /**
     * Select the component from component list or preview freame. Plugins need to
     * handle their own select state.
     */
    const setSelectedComponent = useCallback((component, options) => {
        var _a, _b;
        const { shouldSendIframe = true } = options || {};
        const previousComponent = selectedComponentRef.current;
        // get component from siteComponentMap in case component object is a copy
        component = component ? siteComponentMap[component.id] : undefined;
        // copy component and reassociate with parent (for notifying component list refresh)
        const index = ((_a = component === null || component === void 0 ? void 0 : component.parent) === null || _a === void 0 ? void 0 : _a.children)
            ? component.parent.children.findIndex(c => c.id === component.id)
            : -1;
        if (component) {
            component = Object.assign({}, component);
            if (((_b = component === null || component === void 0 ? void 0 : component.parent) === null || _b === void 0 ? void 0 : _b.children) && index > -1)
                component.parent.children[index] = component;
        }
        selectedComponentRef.current = component;
        // console.log('setSelectedComponent', component);
        if (!isIframe()) {
            setConfigureComponent(component); // notify config panel
        }
        if (component) {
            notifyComponentUpdate(component);
        }
        // NOTE: below sending updates to previous selected could introduce old cached data
        // if (previousComponent && previousComponent.id !== component?.id) {
        //   console.log('notify previous component');
        //   notifyComponentUpdate(previousComponent);
        // }
        if ((component === null || component === void 0 ? void 0 : component.id) !== (previousComponent === null || previousComponent === void 0 ? void 0 : previousComponent.id) && shouldSendIframe) {
            sendSelectedComponent(component);
        }
    }, [siteComponentMap, notifyComponentUpdate, sendSelectedComponent]);
    /**
     * Set the hover component when mouse over on component list or preview frame.
     * Plugins need to handle their own hover state. See hoverProps for helpers.
     */
    const setHoverComponent = useCallback((component, options) => {
        // get component from siteComponentMap in case component object is a copy
        component = component ? siteComponentMap[component.id] : undefined;
        const { shouldSendIframe = true } = options || {};
        const previousComponent = hoverComponentRef.current;
        hoverComponentRef.current = component;
        // remove hover from previous elements
        const hoverClass = 'component-hover';
        for (const el of document.querySelectorAll(`.${hoverClass}`)) {
            el.classList.remove(hoverClass);
        }
        // apply hover styling
        if (component) {
            const selector = `[data-component-id="${component.id}"]`;
            for (const el of document.querySelectorAll(selector)) {
                el.classList.add(hoverClass);
            }
        }
        if ((component === null || component === void 0 ? void 0 : component.id) !== (previousComponent === null || previousComponent === void 0 ? void 0 : previousComponent.id) && shouldSendIframe) {
            sendHoverComponent(component);
        }
    }, [siteComponentMap, sendHoverComponent]);
    // helper methods for checking if a component is selected or mouse over
    const isSelected = useCallback((component) => { var _a; return component.id === ((_a = selectedComponentRef.current) === null || _a === void 0 ? void 0 : _a.id); }, []);
    const isHover = useCallback((component) => { var _a; return component.id === ((_a = hoverComponentRef.current) === null || _a === void 0 ? void 0 : _a.id); }, []);
    const hoverProps = (component) => ({
        'data-component-id': component.id
    });
    return {
        siteConfigState,
        editing,
        site,
        editPath,
        viewMode,
        selectedComponentRef,
        hoverComponentRef,
        selectedRootComponent,
        configureComponent,
        hoverProps,
        toggleEdit,
        setSite,
        setEditPath,
        setViewMode,
        setIframeReady,
        setSiteConfigState,
        setConfigureComponent,
        setSelectedComponent,
        setHoverComponent,
        isSelected,
        isHover
    };
}
//# sourceMappingURL=useSiteConfigurator.js.map